/**
 * Subscription-related models and interfaces
 */

/**
 * Mirrors the backend `BillingAccessState` enum. Distinct from Stripe's own
 * subscription status — see `Subscription.accessState`.
 */
export type BillingAccessState =
  | 'active'
  | 'trialing'
  | 'payment_processing'
  | 'past_due'
  | 'suspended'
  | 'canceled';

export interface SubscriptionTier {
  uid: string;
  code: string;
  name: string;
  description?: string;
  tierType: 'gp' | 'lp';
  isActive: boolean;
  annualPriceEur?: number | null;
  monthlyPriceEur?: number | null;
  /** GP tiers only (KAP-1849) - LP DDQ upload quota this tier grants. */
  lpDdqUploadsPerMonth?: number | null;
  trialPeriodDays?: number;
  isTrialAvailable?: boolean;
  availableAddons?: SubscriptionAddon[];
  createdAt?: string;
  modifiedAt?: string;
}

export interface SubscriptionAddon {
  uid: string;
  code: string;
  name: string;
  description?: string;
  addonType: 'featured_fund' | 'due_diligence' | 'placement_agent';
  isActive: boolean;
  createdAt?: string;
  modifiedAt?: string;
}

/** GP own-fund DDQ monthly quota (common/subscription/current/ and profile usage). */
export interface GpOwnFundDdqQuota {
  limit: number;
  usedThisMonth: number;
  remaining: number;
  month: number;
  year: number;
}

export interface Subscription {
  uid: string;
  emittentCompany?: string;
  investorCompany?: string;
  tier: {
    uid: string;
    code: string;
    name: string;
    description?: string;
    tierType: 'gp' | 'lp';
  };
  addons?: SubscriptionAddon[];
  activeAddons?: SubscriptionAddon[];
  /** Present for GP subscriptions from `common/subscription/current/`. */
  gpOwnFundDdqQuota?: GpOwnFundDdqQuota | null;
  usage?: UsageStatistics | null;
  startDate: string;
  trialEndDate?: string;
  endDate?: string | null;
  isActive: boolean;
  isTrial?: boolean;
  status: 'active' | 'trial' | 'inactive' | 'cancelled' | 'expired';
  isTrialPeriod?: boolean;
  daysRemainingInTrial?: number;
  trialPeriodDays?: number;
  billingInterval?: 'annual' | 'monthly';
  /** Mirrors Stripe. Prefer `accessState` for anything user-facing. */
  paymentStatus?: string;
  /**
   * What the customer actually has access to right now. Stripe reports a SEPA
   * subscription as `active` as soon as the PaymentIntent is `processing`, days
   * before the funds clear and access is granted — so `paymentStatus` alone will
   * claim a plan is live when it is not.
   */
  accessState?: BillingAccessState;
  /** True while a paid tier or add-on is waiting on its first SEPA debit. */
  pendingActivation?: boolean;
  /** Reason from the bank for the most recent failed debit, if any. */
  paymentFailureMessage?: string;
  /**
   * The trial started without SEPA details (KAP-1897) and none are on file yet.
   * Without them the trial ends on the Free tier, so the UI asks for them.
   */
  paymentMethodRequired?: boolean;
  hasStripeBilling?: boolean;
  stripeSubscriptionId?: string;
  pendingPlanChange?: PendingPlanChange | null;
  createdAt?: string;
  modifiedAt?: string;
}

/** Access states that represent access the org already has and is paying for. */
const USABLE_ACCESS_STATES: BillingAccessState[] = [
  'active',
  'trialing',
  'payment_processing',
];

export function hasUsableSubscriptionAccess(subscription: Subscription | null | undefined): boolean {
  if (!subscription) {
    return false;
  }

  const accessOrPaymentState = (subscription.accessState || subscription.paymentStatus || '').toLowerCase();
  if (accessOrPaymentState) {
    return USABLE_ACCESS_STATES.includes(accessOrPaymentState as BillingAccessState);
  }

  return subscription.isActive !== false && (subscription.status === 'active' || subscription.status === 'trial');
}

/**
 * Tier codes for the free tier that the backend auto-assigns on client/org
 * creation (`lp_free`/`gp_free`). This is not a plan the client chose — it is the
 * default state before any plan selection. Kept lowercase for case-insensitive matching.
 */
export const FREE_TIER_CODES: readonly string[] = ['free', 'lp_free', 'gp_free'];

/** True when the subscription is on the auto-assigned free tier (or has no resolved tier). */
export function isFreeTierSubscription(subscription: Subscription | null | undefined): boolean {
  const code = (subscription?.tier?.code || '').toLowerCase();
  return code === '' || FREE_TIER_CODES.includes(code);
}

/**
 * True only when the org has usable access via a *paid* plan it actively chose.
 *
 * A brand-new client is auto-assigned the free tier (`lp_free`/`gp_free`), which
 * `common/subscription/current/` reports with `accessState === 'active'`. That makes
 * {@link hasUsableSubscriptionAccess} return true even though the client has never
 * picked a plan — so callers gating plan-selection UI must use this instead, or they
 * will suppress the plan dialog for exactly the clients who still need to choose.
 */
export function hasPaidSubscriptionAccess(subscription: Subscription | null | undefined): boolean {
  return hasUsableSubscriptionAccess(subscription) && !isFreeTierSubscription(subscription);
}

/**
 * Status payload from GET `subscriptions/onboarding-contract/status/` (KAP-1875).
 * Not part of `Subscription` / `current/` yet — gate onboarding via this endpoint.
 */
export interface OnboardingContractStatus {
  isSigned: boolean;
  isOrgSigned: boolean;
  envelopeSigningEmbed: string | null;
  /**
   * KAP-1867: false when the organization's partner is not a DDQ partner - there is no
   * contract to sign and nothing gates the tier flow. Absent means required.
   */
  required?: boolean;
}

/**
 * Create/fetch payload from POST `subscriptions/onboarding-contract/` (KAP-1875).
 * When already signed the API returns 200 with a null embed.
 */
export interface OnboardingContractCreateResult {
  isSigned: boolean;
  /** Present on POST; derived from isSigned when the API omits is_org_signed. */
  isOrgSigned?: boolean;
  envelopeSigningEmbed: string | null;
  /** See OnboardingContractStatus.required. */
  required?: boolean;
}

/**
 * True when the org may proceed — aligned with the KAP-1874 backend gate: the contract is
 * signed (`is_org_signed`), or (KAP-1867) the org does not need one because its partner is
 * not a DDQ partner. Despite the name this is "may proceed", so every contract check in the
 * upgrade dialog skips the signing step for those orgs.
 */
export function hasSignedOnboardingContract(
  status: OnboardingContractStatus | OnboardingContractCreateResult | null | undefined,
): boolean {
  if (!status) {
    return false;
  }
  if (status.required === false) {
    return true;
  }
  if (status.isSigned === true) {
    return true;
  }
  return 'isOrgSigned' in status && status.isOrgSigned === true;
}

export interface PendingPlanChange {
  pendingTier: {
    uid: string;
    code: string;
    name: string;
  };
  scheduledChangeAt: string | null;
  scheduledChangeType: 'upgrade' | 'downgrade' | null;
}

export interface PlanChangePreview {
  currentTier: {
    uid: string;
    code: string;
    name: string;
  };
  targetTier: {
    uid: string;
    code: string;
    name: string;
  };
  billingInterval: 'annual' | 'monthly';
  changeType: 'upgrade' | 'downgrade';
  outstandingCredit: number;
  upgradePayableNow: number;
  currentPeriodPrice: number;
  targetPeriodPrice: number;
  daysRemaining: number;
  daysTotal: number;
  remainingFraction: number;
  /** Issuer upgrades only: the current plan was never paid, so the new one starts today at full price. */
  startsFresh?: boolean;
  /** Investor change during a free trial: nothing is charged now and the trial runs on (KAP-1897). */
  isTrial?: boolean;
  /** When that trial ends (ISO timestamp); null outside a trial. */
  trialEnd?: string | null;
}

export interface TrialStatus {
  isTrialPeriod: boolean;
  trialEndDate: string | null;
  daysRemaining: number | null;
}

export interface UsageStatistics {
  ddqRunsThisMonth: number;
  remainingDdqRuns: number | null;
  imGenerationsTotal: number;
  canGenerateIm?: boolean;
  month?: number;
  year?: number;
}

export interface FeatureAccess {
  feature: string;
  hasAccess: boolean;
  remainingRuns?: number;
}
